/**
 * The office's reply to a Details Needed ask, as pure rules: what the form
 * starts with, when it may be sent, and what it sends. The modal
 * (InfoAskReplyModal) is the frame; this is what it means.
 *
 * Every asked field is answered or marked "I don't have this"; a reply that
 * provides nothing needs a note saying why (the FTC must hear something).
 */

const ADDRESS_PARTS = ["address", "city", "state", "zip_code"];

function fieldsOf(ask) {
  return (ask && ask.fields) || [];
}

/** The form's starting values: what we hold today for each asked field. */
export function initialDrafts(ask) {
  const current = (ask && ask.current) || {};
  const out = {};
  fieldsOf(ask).forEach(function (field) {
    const value = current[field.key];
    if (field.input === "address") {
      const parts = {};
      ADDRESS_PARTS.forEach(function (part) { parts[part] = (value && value[part]) || ""; });
      out[field.key] = parts;
    } else {
      out[field.key] = value == null ? "" : String(value);
    }
  });
  return out;
}

export function isAnswered(field, value) {
  if (field.input === "address") return !!(value && String(value.address || "").trim());
  return String(value == null ? "" : value).trim() !== "";
}

/** Whether the reply can be sent: nothing asked is left blank and unmarked,
 *  and a reply that provides nothing carries a note. */
export function canSubmit(ask, drafts, skipped, note) {
  const fields = fieldsOf(ask);
  const isSkipped = function (field) { return (skipped || []).indexOf(field.key) !== -1; };
  const open = fields.filter(function (field) { return !isSkipped(field) && !isAnswered(field, (drafts || {})[field.key]); });
  if (open.length) return false;
  const answered = fields.filter(function (field) { return !isSkipped(field) && isAnswered(field, (drafts || {})[field.key]); });
  if (!answered.length && !String(note || "").trim()) return false;
  return true;
}

/** `{ answers, skipped, note }` for POST .../resubmit/. */
export function buildPayload(ask, drafts, skipped, note) {
  const answers = {};
  const skippedKeys = [];
  fieldsOf(ask).forEach(function (field) {
    const value = (drafts || {})[field.key];
    if ((skipped || []).indexOf(field.key) !== -1) {
      skippedKeys.push(field.key);
      return;
    }
    if (!isAnswered(field, value)) return;
    if (field.input === "address") {
      const parts = {};
      ADDRESS_PARTS.forEach(function (part) { parts[part] = String((value && value[part]) || "").trim(); });
      answers[field.key] = parts;
    } else {
      answers[field.key] = String(value).trim();
    }
  });
  return { answers: answers, skipped: skippedKeys, note: String(note || "").trim() };
}

/** The line shown when the patient comes from the practice software, whose
 *  nightly sync overwrites what the office types here. */
export function pmsHint(patient) {
  return patient && patient.from_pms
    ? "Patient details come from your practice software and are refreshed from it nightly — please update them there too."
    : "";
}
